/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';

import { FACT_NAME_LABEL_MAP } from '@/components/display-page/fact-name-label-map';

import { DateRange, Preset } from './types';

// Helper to create object map from presets: label as key, name as value
export const getPresetLabelToNameMap = (
  presets: Preset[]
): Record<string, string> => {
  return presets.reduce(
    (acc, preset) => {
      acc[preset.label] = preset.name;
      return acc;
    },
    {} as Record<string, string>
  );
};

export const useMostRecentPreset = (factName?: string | null): Preset[] => {
  const factLabel = factName
    ? (FACT_NAME_LABEL_MAP[factName]?.label.toLowerCase() ?? factName)
    : 'items';
  return [
    { name: 'recent10', label: `Last 10 ${factLabel}`, count: 10 },
    { name: 'recent20', label: `Last 20 ${factLabel}`, count: 20 },
    { name: 'recent30', label: `Last 30 ${factLabel}`, count: 30 },
  ];
};

// Define hook to return localized presets
export const usePresets = (): Preset[] => {
  const { messages } = useLanguage();
  const langData = messages?.PromptFilters?.date_range;
  const pathname = usePathname();
  const presets: Preset[] = [
    { name: 'last5mins', label: langData?.last5mins || 'Last 5 minutes' },
    { name: 'lastHour', label: langData?.lastHour || 'Last hour' },
    { name: 'last24hours', label: langData?.last24hours || 'Last 24 hours' },
    { name: 'last48hours', label: langData?.last48hours || 'Last 48 hours' },
    { name: 'last7', label: langData?.last7 || 'Last 7 days' },
    { name: 'last14', label: langData?.last14 || 'Last 14 days' },
    { name: 'last30', label: langData?.last30 || 'Last one month' },
    { name: 'monthToDate', label: langData?.monthToDate || 'Month to date' },
    { name: 'yearToDate', label: langData?.yearToDate || 'Year to date' },
  ];

  const isArchitecturePage = pathname.includes('/architecture');
  if (isArchitecturePage) {
    presets.push({
      name: 'tillLastDiscovered',
      label: langData?.till_last_discovered || 'Till last discovered',
    });
  } else {
    presets.push({
      name: 'allTime',
      label:
        langData?.default_observation_window || 'Default Observation Window',
    });
  }
  return presets;
};

// Refactor getPresetRange to accept the presets as an argument
export const getPresetRange = (
  presetName: string,
  presets: Preset[]
): DateRange => {
  const validPresetName = presetName || 'last7';
  const preset = presets.find(({ name }) => name === validPresetName);

  if (!preset) throw new Error(`Unknown date range preset: ${validPresetName}`);

  const now = new Date();
  const from = new Date(now);
  let to = new Date(now);
  to.setSeconds(59);
  to.setMilliseconds(999);

  switch (preset.name) {
    case 'last5mins':
      from.setMinutes(now.getMinutes() - 5);
      to = new Date(now);
      break;
    case 'lastHour':
      from.setHours(now.getHours() - 1);
      to = new Date(now);
      break;
    case 'last24hours':
      from.setHours(now.getHours() - 24);
      to = new Date(now);
      break;
    case 'last48hours':
      from.setHours(now.getHours() - 48);
      to = new Date(now);
      break;
    case 'last7':
      from.setDate(from.getDate() - 6);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      break;
    case 'last14':
      from.setDate(from.getDate() - 13);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      break;
    case 'last30':
      from.setDate(from.getDate() - 29);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      break;
    case 'monthToDate':
      from.setDate(1);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      break;
    case 'yearToDate':
      from.setMonth(0, 1);
      from.setHours(0, 0, 0, 0);
      to = new Date(now);
      break;

    case 'allTime':
      // Return null dates to indicate "All time"
      return { from: undefined, to: undefined };
    // case 'custom'
  }

  return { from, to };
};
